import { ArrowRight } from 'lucide-react';

import { WorkIndex } from '../../../../features/home/components/WorkIndex';
import { TestimonialCard } from '../../../../features/testimonials/components/TestimonialCard';
import { useI18n } from '../../../../shared/i18n';
import { localePath } from '../../../../shared/i18n/routing';
import { htmlToPlainText, useData } from '../../../../shared/lib';
import { Button, Container, Eyebrow, Grid, Section } from '../../../../shared/ui';
import type { HomeData } from './+data';

/**
 * The headline with its last word set on an Amber block — the identity's carry,
 * spent once, on the word that matters. Works for any headline an editor writes,
 * in either direction: the last word is the last one in reading order.
 */
function Headline({ text }: { text: string }) {
  const words = text.trim().split(/\s+/);
  const last = words.pop() ?? '';
  return (
    <>
      {words.length > 0 ? `${words.join(' ')} ` : null}
      <span className="inline-block rounded-[0.22em] bg-carry px-[0.16em] leading-[1.04]">
        {last}
      </span>
    </>
  );
}

/**
 * The landing page: the Studio Index.
 *
 *   1. A huge headline, one Amber word, and a short way in.
 *   2. One list of everything we make and everything we offer. Move along it and
 *      it lights up and shows you.
 *   3. Four habits we keep on every project.
 *   4. Who else trusted us, and the small worries.
 *   5. One loud, warm invitation.
 *
 * The page is light, flat and typographic: the type does the work and the
 * Amber is spent sparingly. Every block that comes from content is optional and
 * hidden when its data is empty, so a brand-new install renders the hero, the
 * principles and the contact invitation, and is meant to look intentional rather
 * than broken (docs/features.md §1). The work tab's label stays team-attributing
 * ("Selected work", not "projects we delivered"): the list can contain prior
 * work, and LEG-04 applies to that wording exactly as it does to the portfolio
 * listing's — each project row also carries its attribution badge.
 *
 * The hero's metric row is content too: it renders only the tiles an editor has
 * configured. The code itself asserts no customers, figures or leadership.
 */
export default function Page() {
  const { locale, t } = useI18n();
  const {
    hero,
    copy,
    metrics,
    featuredServices,
    featuredProjects,
    testimonials,
    testimonialPhotos,
    faqPreview,
  } = useData<HomeData>();

  const principles = [
    { key: 'Small', n: '01' },
    { key: 'Exact', n: '02' },
    { key: 'Honest', n: '03' },
    { key: 'Together', n: '04' },
  ] as const;

  return (
    <>
      <Section spacing="none" className="pt-14 pb-14 sm:pt-24 sm:pb-20">
        <Container className="flex flex-col gap-12">
          <div className="flex flex-col items-start gap-7">
            <Eyebrow>{copy.kicker || t('common.home.kicker')}</Eyebrow>
            <h1 className="hs-display text-hero leading-[0.94] [text-wrap:wrap]">
              <Headline text={copy.headline || t('common.home.headline')} />
            </h1>
          </div>

          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex max-w-[44ch] flex-col items-start gap-4">
              {/*
              The company's own words when it has set them, the shipped line when
              it has not — `general.tagline` is admin-editable and must win over
              shipped copy wherever it exists.
            */}
              <p className="text-lg text-text-muted">{hero.tagline || t('common.home.lead')}</p>
              {hero.about ? <p className="text-text-muted">{hero.about}</p> : null}
            </div>
            <div className="flex flex-col items-start gap-6 lg:items-end">
              <div className="flex flex-wrap gap-3">
                <Button
                  asChild
                  size="lg"
                  iconEnd={<ArrowRight className="size-4 hs-icon-directional" aria-hidden="true" />}
                >
                  <a href={localePath(locale, '/contact')}>
                    {copy.ctaPrimaryLabel || t('common.home.startProject')}
                  </a>
                </Button>
                <Button asChild size="lg" variant="secondary">
                  <a href={localePath(locale, '/projects')}>
                    {copy.ctaSecondaryLabel || t('common.home.viewWork')}
                  </a>
                </Button>
              </div>
              {metrics.length > 0 ? (
                <dl className="flex flex-wrap gap-x-10 gap-y-4">
                  {metrics.map((metric) => (
                    <div key={metric.label}>
                      <dd dir="ltr" className="text-3xl font-semibold text-text">
                        {metric.value}
                      </dd>
                      <dt className="hs-meta text-text-subtle">{metric.label}</dt>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>
          </div>
        </Container>
      </Section>

      {featuredProjects.length > 0 || featuredServices.length > 0 ? (
        <Section spacing="none" className="pb-24 sm:pb-32">
          <Container>
            <WorkIndex
              projects={featuredProjects}
              services={featuredServices}
              workLabel={copy.projectsHeading || t('common.home.featuredProjects')}
            />
          </Container>
        </Section>
      ) : null}

      <Section spacing="none" className="pb-24 sm:pb-32">
        <Container className="flex flex-col gap-12">
          <div className="flex max-w-3xl flex-col gap-3">
            <Eyebrow>{t('common.home.approachHeading')}</Eyebrow>
            <h2 className="hs-display text-5xl sm:text-6xl">{t('common.home.approachLead')}</h2>
          </div>
          <ol className="hs-reveal grid grid-cols-1 gap-x-16 sm:grid-cols-2">
            {principles.map(({ key, n }) => (
              <li
                key={key}
                className="grid grid-cols-[6rem_1fr] items-baseline gap-5 border-t border-border-strong py-8 sm:grid-cols-[8.5rem_1fr]"
              >
                <span
                  dir="ltr"
                  aria-hidden="true"
                  className="hs-display text-7xl leading-[0.9] text-surface-sunken sm:text-8xl"
                >
                  {n}
                </span>
                <div className="flex flex-col gap-2">
                  <h3 className="text-3xl font-semibold tracking-tight">
                    {t(`common.home.principle${key}Title`)}
                  </h3>
                  <p className="text-lg text-text-muted">{t(`common.home.principle${key}Body`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </Container>
      </Section>

      {testimonials.length > 0 ? (
        <Section spacing="none" className="pb-24 sm:pb-32">
          <Container className="flex flex-col gap-10">
            <h2 className="hs-display max-w-[20ch] text-5xl sm:text-6xl">
              {copy.testimonialsHeading || t('common.home.testimonialsHeading')}
            </h2>
            <Grid columns={3}>
              {testimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  photo={
                    testimonial.photo_media_id
                      ? testimonialPhotos[testimonial.photo_media_id]
                      : undefined
                  }
                />
              ))}
            </Grid>
          </Container>
        </Section>
      ) : null}

      {faqPreview.length > 0 ? (
        <Section spacing="none" className="pb-24 sm:pb-32">
          <Container className="grid grid-cols-1 gap-10 lg:grid-cols-[.8fr_1.2fr] lg:gap-20">
            <div className="flex flex-col items-start gap-5">
              <h2 className="hs-display text-5xl sm:text-6xl">
                {copy.faqHeading || t('common.home.faqPreview')}
              </h2>
              <Button asChild variant="secondary">
                <a href={localePath(locale, '/faq')}>{t('common.home.viewAllFaq')}</a>
              </Button>
            </div>
            <ul className="flex flex-col gap-6">
              {faqPreview.map((entry) => (
                <li key={entry.id} className="border-t border-border-strong pt-6">
                  <p className="text-xl font-semibold text-text">{entry.question}</p>
                  {/*
                    `answer` is rich-text HTML. This is a two-line teaser, so it
                    is flattened to text rather than rendered as markup —
                    printing it raw showed the visitor the tags themselves. The
                    full answer is rendered as HTML on /faq by `FaqEntry`.
                  */}
                  <p className="mt-2 line-clamp-2 text-text-muted">
                    {htmlToPlainText(entry.answer)}
                  </p>
                </li>
              ))}
            </ul>
          </Container>
        </Section>
      ) : null}

      {/*
        The closing invitation: one big Amber panel, centred, with an Ink pill.
        Amber is a sanctioned ground colour in the identity (the "announcement"
        layout); Ink text on it is 10.6:1.
      */}
      <Section spacing="none" className="pb-20 sm:pb-28">
        <Container>
          <div className="flex flex-col items-center gap-6 rounded-[var(--hs-radius-2xl)] bg-carry px-6 py-16 text-center text-carry-fg sm:px-14 sm:py-24">
            <h2 className="hs-display max-w-[16ch] text-5xl sm:text-7xl">
              {copy.closingHeading || t('common.home.contactCta')}
            </h2>
            <p className="max-w-[48ch] text-xl">
              {copy.closingBody || t('common.home.contactBody')}
            </p>
            <Button
              asChild
              size="lg"
              className="mt-2"
              iconEnd={<ArrowRight className="size-4 hs-icon-directional" aria-hidden="true" />}
            >
              <a href={localePath(locale, '/contact')}>
                {copy.closingButtonLabel || t('common.home.getInTouch')}
              </a>
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}
